<template>
  <div class="scroll-slider" :class="{'scroll-slider--loading ' : isLoading}">
    <loader v-if="isLoading"/>
    <video-layer :url="videoUrl" :flowid="flowid" ref="videoLayer">
      <template #closebtn>
        {{ closeText }}
      </template>
    </video-layer>
    <div class="scroll-slider__side-content" v-show="showSideContent" v-if="!isSimpleSlider">
      <slot name="sidecontent" :getSildes="getSildes"/>
    </div>
    <a :id="filteredContentId" tabindex="-1" class="scroll-slider__skip-content"/>
    <scroller
      data-simplebar-auto-hide="false"
      :recalculate="updateScroll"
      :toggle-side-content="!isSimpleSlider"
      v-animate="{ cssClass: 'animate-in', delay: 100, reset: true }"
      ref="slider"
    >
      <slot v-if="!cards" :openVideo="openVideo"/>
      <div v-else class="scroll-slider__slide" focusable-slide v-for="card in cards" :key="card.id">
        <slider-card v-if="card.cardType < 3" :data="card"/>

        <video-card v-else-if="card.cardType === 3" @wasClicked="openVideo({ url: card.apiLink, flowid: card.flowid }, { cardId: card.id })" :data="card" :id="card.id"/>

        <simple-card v-else-if="card.cardType === 5" :data="card" :id="card.id"/>

        <quote-card v-else :data="card"/>
      </div>

      <div v-if="hasError" class="error">Something went wrong :(</div>
    </scroller>
  </div>
</template>

<script>
  import scroller from './components/scroller/scroller.vue';
  import { fetchData } from '@Foundation/services';
  import { animate } from '@Foundation/directives/animate/animate';

  export default {
    name: 'scroll-slider',

    components: {
      scroller,
      'loader': () => import('@Feature/Loader/code/Script/Loader'),
      'video-layer': () => import('@Feature/Video/code/Scripts/VideoLayer/video-layer'),
      'slider-card': () => import('./components/sliderCard/slider-card.vue'),
      'video-card': () => import('./components/videoCard/video-card.vue'),
      'quote-card': () => import('./components/quoteCard/quote-card.vue'),
      'simple-card': () => import('./components/simpleCard/simple-card.vue')
    },

    directives: {
      animate
    },

    props: {
      slidesApiUrl: { type: String, default: '' },
      sliderApiParams: { type: Object, default: () => {
        return {};
      } },
      isSimpleSlider: { type: Boolean, default: false },
      srFilteredText: { type: String, default: '' },
      closeText: { type: String, default: 'close' }
    },

    data() {
      return {
        counter: 0,
        cards: null,
        updateScroll: false,
        showSideContent: true,
        videoUrl: null,
        flowid: null,
        filteredContentId: null,
        srFilterId: null,
        isLoading: false,
        hasError: false
      };
    },

    computed: {
      isFilteredSlider() {
        return !!this.slidesApiUrl;
      }
    },

    mounted() {
      this.makeFocusable();
      this.isFilteredSlider && this.setActiveFilterOnLoad();
    },

    methods: {
      openVideo(data, event) {
        let clickedElement;

        event.target ? clickedElement = event.target : clickedElement = document.getElementById(event.cardId).querySelector('.card__play');

        this.videoUrl = data.url;
        this.flowid = data.flowid;
        setTimeout(() => {
          this.$refs.videoLayer.videoCall(clickedElement);
        }, 100);
      },

      makeFocusable() {
        this.$el.classList.contains('scroll-slider--image') && this.$el.querySelector('.simplebar-content-wrapper').setAttribute('tabindex', 0);
      },

      setSrText(text) {
        document.querySelector('.filtered-results-sr').innerText = text;
      },

      srFilteredResults(amount) {
        this.srFilterId = this.filteredContentId;
        this.setSrText(`${amount} ${this.srFilteredText}`);

        setTimeout(() => {
          this.setSrText('');
        }, 7000);
      },

      addSliderAnimation(type) {
        [...this.$refs.slider.$el.querySelectorAll('.scroll-slider__slide')].forEach((slide, index) => {
          slide.setAttribute('style', this.setAnimation(type, index));
        });
      },

      setAnimation(type, elmIndex) {
        const animateIn = `transition: opacity .4s cubic-bezier(.075, .82, .165, 1) ${elmIndex * 0.05}s, transform .25s cubic-bezier(.075, .82, .165, 1) ${elmIndex * 0.05}s; opacity: 1; transform: none;`;
        const animateOut = `transition: opacity .4s cubic-bezier(.075, .82, .165, 1) ${elmIndex * 0.05}s, transform .25s cubic-bezier(.075, .82, .165, 1) ${elmIndex * 0.05}s; opacity: 0 transform: translateY(40px);`;

        return type === 'in' ? animateIn : animateOut;
      },

      buildApiParams() {
        const params = [];
        for (const [key, value] of Object.entries(this.sliderApiParams)) {
          params.push(`${key}=${value}`);
        }
        return params.join('&');
      },

      setActiveFilterOnLoad() {
        const selectedId = this.$el.querySelector('.scroll-slider__filters .skipto').getAttribute('href').slice(1);
        this.setAriaSelected(this.$el.querySelector('.scroll-slider__filters button'), true);
        this.filteredContentId = selectedId;
      },

      setAriaSelected(el, state) {
        el.setAttribute('aria-selected', state);
      },

      applyFilter(response) {
        this.cards = response;

        setTimeout(() => {
          this.isLoading = false;
          this.updateScroll = true;
          this.addSliderAnimation('in');
        }, 10);

        this.srFilteredResults(this.cards.length);

        this.$nextTick(() => {
          this.updateScroll = false;
        });
      },

      getSildes(id, el) {
        this.isLoading = true;
        const clickedBtn = el.target;
        this.hasError = false;

        if (this.filteredContentId === id) {
          this.isLoading = false;
          return false;
        }

        this.filteredContentId = id;
        this.counter += 1;
        const innerCounter = this.counter;

        /**
         * remove active state from filter links
         */
        this.$el.querySelectorAll('.scroll-slider__side-content button').forEach(filterCta => {
          this.setAriaSelected(filterCta, false);
        });

        /**
         * add active state to filter links
         */
        this.setAriaSelected(clickedBtn, true);

        /** animation leave*/
        this.addSliderAnimation('out');
        this.$refs.slider.$el.classList.remove('animated');

        setTimeout(() => {
          /**
           * API link to test filters on fractal:
           * `/api/scrollSlider/slides?category=${id}`
           */
          this.counter === innerCounter && fetchData(`${this.slidesApiUrl}${this.filteredContentId}&${this.buildApiParams()}`)
            .then((jsonData) => {
              this.applyFilter(jsonData);
              this.isLoading = false;
            })
            .catch(() => {
              this.hasError = true;
              this.isLoading = false;
            });
        }, 1000);
      }
    }
  };
</script>

<style lang="scss" src="./scroll-slider.scss"></style>
